import React from 'react';
import ProgressBar from '../ProgressBar';
import NotificationOptInBanner from '../NotificationOptInBanner';
import { isRenderInFlight } from '../../lib/editDraft';

export const SUPPORT_EMAIL = 'support@tweediastudio.com';

// `editedVideo.error` is written straight from the render worker, so it is
// normally an engine diagnostic ("FFmpeg exited with code 1") that means nothing
// to the person who just lost their video — and reads like they broke something.
// Those get the generic apology below, with the raw string folded away under
// "Technical details" for support. (#583)
//
// The exception is this allowlist of error CODES whose backend `error` string
// was deliberately written FOR the user. For those the backend's own words
// become the headline and the details block is suppressed — restating the copy
// here would give us two sources that drift the moment either side is reworded,
// and would show the user the same sentence twice (once as the headline, once
// inside Technical details, which prints `error` verbatim).
const USER_FACING_ERROR_CODES = new Set([
    // Written by _claim_project_for_processing() in the render container
    // (packages/containers/generatevideo/processing/video_editing.py): "Your
    // account is over its storage allowance. Free up space or upgrade your plan
    // to render new videos." The one render failure the user can clear
    // themselves.
    'ACCOUNT_STORAGE_FROZEN',
]);

const GENERIC_FAILURE_MESSAGE = "Something went wrong on our end while rendering — nothing you set up caused this. Your footage and settings are untouched, so you can press Create Video to try again.";

// True only when the backend both flagged user-facing copy AND actually sent a
// string. A document carrying the code with no `error` — a partial write, or a
// re-queue that cleared one field — must fall back to the generic apology
// rather than render an empty headline.
export const hasUserFacingError = (editedVideo = {}) =>
    USER_FACING_ERROR_CODES.has(editedVideo.errorCode) && Boolean(editedVideo.error);

export const failureMessage = (editedVideo = {}) =>
    (hasUserFacingError(editedVideo) ? editedVideo.error : GENERIC_FAILURE_MESSAGE);

// Three distinct states, and saying the wrong one costs trust either way.
// `refundIssued` is set by the billing trigger (_reconcile_edited_video in
// packages/functions/billing/background/main.py) only AFTER the refund lands,
// so it is the only field that licenses the past tense. Until then the render
// may still be mid-retry — the worker leaves status 'failed' between attempts —
// so we promise the refund rather than claim it. A free render
// (creditsDeducted 0) gets no credit line at all.
export const refundNotice = (editedVideo = {}) => {
    if (editedVideo.refundIssued) {
        return 'Your credit has been refunded — it is already back in your balance.';
    }
    if (Number(editedVideo.creditsDeducted) > 0) {
        return "Your credit will be refunded automatically — you are never charged for a video you didn't get.";
    }
    return null;
};

const RenderStatusPanels = ({
    currentProject,
    generationEta,
    stoppingTask,
    onStopClick,
    progressRef,
}) => {
    const editedVideo = currentProject.editedVideo;
    const refundText = editedVideo ? refundNotice(editedVideo) : null;

    return (
        <>
            {isRenderInFlight(editedVideo) &&
                <div className="pv-full-width" ref={progressRef}>
                    <p className="pv-status-label">{editedVideo.status === 'queued' ? 'Queued for Generation...' : 'Creating Video'}</p>
                    <ProgressBar
                        progress={editedVideo.status === 'queued' ? 100 : editedVideo.progress}
                        shimmer={editedVideo.status === 'queued'}
                        timeRemaining={editedVideo.status === 'processing' ? generationEta : null}
                        stopButton={
                            <button
                                className="btn-stop"
                                disabled={stoppingTask === 'generation'}
                                onClick={() => onStopClick('generation')}
                            >
                                <span className="stop-icon"></span>
                                {stoppingTask === 'generation' ? 'Stopping...' : 'Stop'}
                            </button>
                        }
                    />
                    <NotificationOptInBanner event="generation_complete" />
                </div>
            }
            {editedVideo && editedVideo.status === 'failed' &&
                <div className="pv-full-width pv-render-failed">
                    {/*
                      A live region because this panel REPLACES the progress bar the
                      user has been watching: without one a screen reader announces
                      nothing when the render dies, so the failure copy — and the
                      refund line, the part that matters most — is silent.

                      role="status" (polite), NOT role="alert" (assertive). As the
                      refundNotice comment above says, status 'failed' is also
                      written BETWEEN retry attempts — _handle_edit_with_retries in
                      packages/containers/generatevideo/api/main.py loops up to
                      MAX_RETRIES, and each attempt writes 'failed' before
                      re-raising. ProjectView's onSnapshot delivers that
                      intermediate state, so this panel can appear for a render that
                      then succeeds. Assertive would INTERRUPT the user mid-sentence
                      with a false "your credit will be refunded"; polite queues
                      behind them and is still announced.

                      Scoped to the copy, with <details> deliberately OUTSIDE it:
                      live regions are aria-atomic by default, so a disclosure
                      inside would re-announce the entire panel every time the user
                      toggled it.
                    */}
                    <div role="status">
                        <h4 className="pv-render-failed-title">We couldn't finish this video</h4>
                        <p className="pv-render-failed-message" data-testid="render-failure-message">{failureMessage(editedVideo)}</p>
                        {refundText && <p className="pv-render-failed-credit" data-testid="render-failure-credit">{refundText}</p>}
                        <p className="pv-render-failed-support">
                            Keeps happening? Email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> and we'll dig into it.
                        </p>
                    </div>
                    {editedVideo.error && !hasUserFacingError(editedVideo) &&
                        <details className="pv-render-failed-details" data-testid="render-failure-details">
                            <summary className="pv-render-failed-details-summary">Technical details</summary>
                            <code className="pv-render-failed-error">{editedVideo.error}</code>
                        </details>
                    }
                </div>
            }
            {editedVideo && editedVideo.status === 'cancelled' &&
                <div className="pv-full-width pv-task-cancelled">
                    <span className="pv-task-cancelled-text">Generation stopped — credits refunded</span>
                </div>
            }
        </>
    );
};

export default RenderStatusPanels;
